import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, PluginView, TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";

const PLUGIN_KEY = new PluginKey("tableControls");

const ADD_ROW_CLASS = "table-add-row-btn";
const ADD_COL_CLASS = "table-add-col-btn";

const getEditorRoot = (view: EditorView): Element | null =>
  view.dom.closest(".skill-collection-markdown-editor");

const createButton = (className: string, title: string): HTMLButtonElement => {
  const btn = document.createElement("button");
  btn.className = className;
  btn.type = "button";
  btn.title = title;
  btn.contentEditable = "false";
  btn.textContent = "+";
  return btn;
};

const selectLastCellInRow = (view: EditorView, row: Element) => {
  const lastCell = row.querySelector("td:last-child, th:last-child");
  if (lastCell) {
    const pos = view.posAtDOM(lastCell, 0);
    const $pos = view.state.doc.resolve(pos);
    view.dispatch(view.state.tr.setSelection(TextSelection.near($pos)));
  }
};

const attachTableControls = (
  view: EditorView,
  addRowAfter: () => void,
  addColumnAfter: () => void,
) => {
  if (!view.editable) {
    return;
  }

  const editorDom = getEditorRoot(view);
  if (!editorDom) {
    return;
  }

  const tables = editorDom.querySelectorAll("table");

  tables.forEach(table => {
    const wrapper = table.parentElement;
    if (!wrapper) {
      return;
    }

    if (getComputedStyle(wrapper).position === "static") {
      (wrapper as HTMLElement).style.position = "relative";
    }

    if (!wrapper.querySelector(`.${ADD_ROW_CLASS}`)) {
      const addRowBtn = createButton(ADD_ROW_CLASS, "Add row");
      addRowBtn.addEventListener("mousedown", e => {
        e.preventDefault();
        e.stopPropagation();
        const lastRow = table.querySelector("tr:last-child");
        if (lastRow) {
          selectLastCellInRow(view, lastRow);
        }
        addRowAfter();
      });
      wrapper.appendChild(addRowBtn);
    }

    if (!wrapper.querySelector(`.${ADD_COL_CLASS}`)) {
      const addColBtn = createButton(ADD_COL_CLASS, "Add column");
      addColBtn.addEventListener("mousedown", e => {
        e.preventDefault();
        e.stopPropagation();
        const firstRow = table.querySelector("tr:first-child");
        if (firstRow) {
          selectLastCellInRow(view, firstRow);
        }
        addColumnAfter();
      });
      wrapper.appendChild(addColBtn);
    }
  });
};

const cleanupOrphanedControls = (view: EditorView) => {
  const editorDom = getEditorRoot(view);
  if (!editorDom) {
    return;
  }

  editorDom.querySelectorAll(`.${ADD_ROW_CLASS}, .${ADD_COL_CLASS}`).forEach(btn => {
    const wrapper = btn.parentElement;
    if (!wrapper || !wrapper.querySelector("table")) {
      btn.remove();
    }
  });
};

const removeAllTableControlButtons = (view: EditorView) => {
  const editorDom = getEditorRoot(view);
  if (!editorDom) {
    return;
  }

  editorDom.querySelectorAll(`.${ADD_ROW_CLASS}, .${ADD_COL_CLASS}`).forEach(btn => {
    btn.remove();
  });
};

export const TableControls = Extension.create({
  name: "tableControls",

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: PLUGIN_KEY,
        view: (): PluginView => ({
          update: (editorView: unknown) => {
            cleanupOrphanedControls(editorView as EditorView);

            if (!editor.isEditable) {
              removeAllTableControlButtons(editorView as EditorView);
              return;
            }

            requestAnimationFrame(() => {
              if (!editor.isEditable) {
                return;
              }

              attachTableControls(
                editorView as EditorView,
                () => editor.chain().focus().addRowAfter().run(),
                () => editor.chain().focus().addColumnAfter().run(),
              );
            });
          },
        }),
      }),
    ];
  },
});
